// Reducer for topic actions
import { saveAs } from 'file-saver'

import * as ActionTypes from '../constants/action_types'
import { DefaultTopicsListState, topicsList, topicsCount, DefaultSearchListState } from './topicsList_reducer'
import { topicDetails } from './topicDetails_reducer'
import { topicPlan, topicPlanAnticipation } from './topicPlans_reducer'
import { topicMetrics } from './topicMetrics_reducer'
import { topicSegments } from './topicSegments_reducer'
import { topicDemographics } from './topicDemographics_reducer'
import { topicMediaPlan } from './topicMediaPlan_reducer'
import * as f from '../classes/Filters'
import { setFilters } from './topicFilter_reducer'
import { DefaultState } from './DefaultState'
//import { PlanFilters } from '../classesf.Filters.Common./FilterHelpers'

// -----------------------------------------------------------------------------
// Reducer for lists of topics (entities and video channels)

export const DefaultTopicsState = {
  list: { ...DefaultTopicsListState },
  count: null,
  byId: {}
}

export const DefaultSearchState = {
  list: { ...DefaultSearchListState },
  byId: {}
}

export const DefaultFilterState = {
  list: { ...DefaultSearchListState }
}

const TopicSubTypes = 'Movie Show People Brand Music Lifestyle Channel Gaming'.split(' ')
export const DefaultFilters = new f.Filters(TopicSubTypes.map(subType => new f.Filter({
  topic_sub_type: subType
})))

/* Default state for the plan node */
export const DefaultTopicPlanTopLevelState = {
  currentFilters: DefaultFilters,
  selectedFilters: {}
  // Dyanmic keys here, one per Filters
}

/* Default state for the plan node */
export const DefaultTopicPlanAnticipationTopLevelState = {
  currentFilters: f.Filters.Common.Anticipation, //PlanFilters.Anticipation,
  selectedFilters: {},
  currentPage: 1,
  endOfResult: false
  // Dyanmic keys here, one per Filters
}

/* Default state for a plan based on a set of filters */
export const DefaultTopicPlanFilteredState = {
  currentPage: 1,
  endOfResult: false
  // Dynamic keys here starting with 1, 2, 3 (one per page essentially)
}

/* Default state for the export sub-node of a plan. */
export const DefaultTopicPlanExportState = { ...DefaultState }

/* Reducer for the topics.byId.ID.plan.FILTER.PAGE actions */
export const topicByIdPlanFilterPage = (state, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_TOPIC_PLAN:
    case ActionTypes.RECEIVE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN:
      return topicPlan(state, action)
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.RECEIVE_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_ANTICIPATION:
      return topicPlanAnticipation(state, action)
    default:
      return state
  }
}

/* Reducer for the topics.byId.ID.plan.FILTER actions */
export const topicByIdPlanFilter = (state = DefaultTopicPlanFilteredState, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_TOPIC_PLAN:
    case ActionTypes.RECEIVE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.RECEIVE_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_ANTICIPATION:
      if (action.page) {
        // Compute endOfResult
        let endOfResult = state.endOfResult
        if (action.response && action.response.recommendations) {
          endOfResult = action.response.recommendations.length === 0
        }
        return {
          ...state,
          currentPage: action.page,
          endOfResult,
          [action.page]: topicByIdPlanFilterPage(state[action.page], action)
        }
      } else {
        console.warn('Topic Plan Page action fired without a page:', action)
        return state
      }
    default:
      return state
  }
}

export const topicByIdPlanExport = (state = DefaultTopicPlanExportState, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT:
      return {
        ...state,
        isFetching: true
      }
    case ActionTypes.RECEIVE_TOPIC_PLAN_EXPORT:
      saveAs(action.response, action.filename)
      return {
        ...state,
        isFetching: false,
        lastUpdated: action.receivedAt
      }
    case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.error,
        lastUpdated: action.receivedAt
      }
    default:
      return state
  }
}

/* Reducer for the topics.byId.ID.plan actions */
export const topicsByIdPlan = (state = DefaultTopicPlanTopLevelState, action) => {
  switch (action.type) {
    case ActionTypes.SET_TOPIC_PLAN_FILTERS:
      return setFilters(state, action)
    case ActionTypes.SET_TOPIC_PLAN_RESET_FILTERS:
      return {
        ...state,
        selectedFilters: {}
      }
    case ActionTypes.REQUEST_TOPIC_PLAN:
    case ActionTypes.RECEIVE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN:
      const filterKey = action.filters || DefaultFilters
      return {
        ...state,
        filteredModule: action.module,
        [action.module]: filterKey,
        filterSelected: false,
        filterApplied: true,
        [filterKey]: topicByIdPlanFilter(state[filterKey], action)
      }
      case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT:
      case ActionTypes.RECEIVE_TOPIC_PLAN_EXPORT:
      case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT_FAILURE:
        return {
          ...state,
          export: topicByIdPlanExport (state.export, action)
        }
    default:
      return state
  }
}

export const topicsByIdPlanAnticipation = (state = DefaultTopicPlanAnticipationTopLevelState, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.RECEIVE_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_ANTICIPATION:
      const filterKey = 'anticipationPlan'
      return {
        ...state,
        [filterKey]: topicByIdPlanFilter(state[filterKey], action)
      }
    default:
      return state
  }
}

export const searchByKeword = (state = DefaultSearchState, action) => {
  switch (action.type) {
    case ActionTypes.SEARCH_RESULT_REQUEST:
    case ActionTypes.SEARCH_RESULT_SUCCESS:
      if (action.response) {
        return {
          ...state,
          isFetching: false,
          details: action.response.topics
        }
      } else {
        return state
      }
    case ActionTypes.SEARCH_RESULT_FAILURE:
    case ActionTypes.SEARCH_RESULT_CLEAR:
    default:
      return state
}
}

export const libraryFilter = (state = DefaultFilterState, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_FILTER:
    case ActionTypes.SUCCESS_FILTER:
    if (action.response) {
      return {
        ...state,
        isFetching: false,
        details: action.response
      }
    } else {
      return state
    }
    case ActionTypes.REQUEST_FILTER_FAILURE:
    case ActionTypes.RECEIVE_FILTER:
    default:
      return state
}
}

/* Reducer for the topics.byId.ID node */
export const topicsById = (state = {}, action) => {
  switch (action.type) {
      case ActionTypes.REQUEST_TOPIC_DETAILS:
      case ActionTypes.RECEIVE_TOPIC_DETAILS:
      case ActionTypes.REQUEST_TOPIC_DETAILS_FAILURE:
      case ActionTypes.INVALIDATE_TOPIC_DETAILS:
        return {
          ...state,
          details: topicDetails(state.details, action)
        }
    case ActionTypes.REQUEST_TOPIC_PLAN_METRICS:
    case ActionTypes.RECEIVE_TOPIC_PLAN_METRICS:
    case ActionTypes.REQUEST_TOPIC_PLAN_METRICS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_METRICS:
      return {
        ...state,
        metrics: topicMetrics(state.metrics, action)
      }
    case ActionTypes.REQUEST_TOPIC_PLAN_SEGMENTS:
    case ActionTypes.RECEIVE_TOPIC_PLAN_SEGMENTS:
    case ActionTypes.REQUEST_TOPIC_PLAN_SEGMENTS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_SEGMENTS:
      return {
        ...state,
        segments: topicSegments(state.metrics, action)
      }
    case ActionTypes.REQUEST_TOPIC_PLAN_DEMOGRAPHICS:
    case ActionTypes.RECEIVE_TOPIC_PLAN_DEMOGRAPHICS:
    case ActionTypes.REQUEST_TOPIC_PLAN_DEMOGRAPHICS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_DEMOGRAPHICS:
      return {
        ...state,
        demographics: topicDemographics(state.demographics, action)
      }
    case ActionTypes.SET_TOPIC_PLAN_FILTERS:
    case ActionTypes.SET_TOPIC_PLAN_RESET_FILTERS:
    case ActionTypes.REQUEST_TOPIC_PLAN:
    case ActionTypes.RECEIVE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT:
    case ActionTypes.RECEIVE_TOPIC_PLAN_EXPORT:
    case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT_FAILURE:
      return {
        ...state,
        plan: topicsByIdPlan(state.plan, action)
      }
      case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION:
      case ActionTypes.RECEIVE_TOPIC_PLAN_ANTICIPATION:
      case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION_FAILURE:
      case ActionTypes.INVALIDATE_TOPIC_PLAN_ANTICIPATION:
        return {
          ...state,
          anticipationPlan: topicsByIdPlanAnticipation(state.anticipationPlan, action)
        }
      case ActionTypes.REQUEST_TOPIC_MEDIA_PLAN:
      case ActionTypes.RECEIVE_TOPIC_MEDIA_PLAN:
      case ActionTypes.REQUEST_TOPIC_MEDIA_PLAN_FAILURE:
      case ActionTypes.INVALIDATE_TOPIC_MEDIA_PLAN:
        return {
          ...state,
          mediaPlan: topicMediaPlan(state.mediaPlan, action)
        }
    default:
      return state
  }
}

export const topics = (state = DefaultTopicsState, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_TOPICS:
    case ActionTypes.REQUEST_TOPICS_FAILURE:
    case ActionTypes.RECEIVE_TOPICS:
    case ActionTypes.INVALIDATE_TOPICS:
      return {
        ...state,
        list: topicsList(state.list, action)
      }
    case ActionTypes.REQUEST_TOPICS_COUNT:
    case ActionTypes.REQUEST_TOPICS_COUNT_FAILURE:
    case ActionTypes.RECEIVE_TOPICS_COUNT:
    case ActionTypes.INVALIDATE_TOPICS_COUNT:
      return {
        ...state,
        count: topicsCount(state.list, action)
      }
    case ActionTypes.REQUEST_TOPIC_DETAILS:
    case ActionTypes.RECEIVE_TOPIC_DETAILS:
    case ActionTypes.REQUEST_TOPIC_DETAILS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_DETAILS:
    case ActionTypes.SET_TOPIC_PLAN_FILTERS:
    case ActionTypes.SET_TOPIC_PLAN_RESET_FILTERS:
    case ActionTypes.REQUEST_TOPIC_PLAN:
    case ActionTypes.RECEIVE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN:
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.RECEIVE_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.REQUEST_TOPIC_PLAN_ANTICIPATION_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_ANTICIPATION:
    case ActionTypes.REQUEST_TOPIC_PLAN_METRICS:
    case ActionTypes.RECEIVE_TOPIC_PLAN_METRICS:
    case ActionTypes.REQUEST_TOPIC_PLAN_METRICS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_METRICS:
    case ActionTypes.REQUEST_TOPIC_PLAN_SEGMENTS:
    case ActionTypes.RECEIVE_TOPIC_PLAN_SEGMENTS:
    case ActionTypes.REQUEST_TOPIC_PLAN_SEGMENTS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_SEGMENTS:
    case ActionTypes.REQUEST_TOPIC_PLAN_DEMOGRAPHICS:
    case ActionTypes.RECEIVE_TOPIC_PLAN_DEMOGRAPHICS:
    case ActionTypes.REQUEST_TOPIC_PLAN_DEMOGRAPHICS_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_PLAN_DEMOGRAPHICS:
    case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT:
    case ActionTypes.RECEIVE_TOPIC_PLAN_EXPORT:
    case ActionTypes.REQUEST_TOPIC_PLAN_EXPORT_FAILURE:
    case ActionTypes.REQUEST_TOPIC_MEDIA_PLAN:
    case ActionTypes.RECEIVE_TOPIC_MEDIA_PLAN:
    case ActionTypes.REQUEST_TOPIC_MEDIA_PLAN_FAILURE:
    case ActionTypes.INVALIDATE_TOPIC_MEDIA_PLAN:
      if (action.topicId) {
        return {
          ...state,
          byId: {
            ...state.byId,
            [action.topicId]: topicsById(state.byId[action.topicId], action)
          }
        }
      } else {
        console.warn('Topic action fired without a topic id:', action)
        return state
      }
  default:
    return state
  }
}
